import { Component, OnInit, ViewChild, Input, Output, EventEmitter, TemplateRef } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { UserAction } from '../../enums/user-action.enum';
import { IUserDetail } from '../../interfaces/user-detail';
import { CheckboxItem } from '../../components/shared/checkbox-group/checkbox-item';
import { RoleService } from '../../services/role.service';
import { IUserDetailResult } from '../../interfaces/user-detail-result';
import { UserService } from '../../services/user.service';
import { IUser } from '../../interfaces/user';

@Component({
    selector: 'app-user-detail',
    templateUrl: './user-detail.component.html',
    styles: [],
    standalone: false
})
export class UserDetailComponent implements OnInit {
  @Input('isDuplicate') isDuplicate: (userId: string) => false;
  @Input('isAlreadyAssigned') isAlreadyAssigned: (user: IUser) => false;
  @Input() applicationAllowLoginHelpMessage: string;
  @Input() accountAllowLoginHelpMessage: string;
  @Input() showAppAllowLogin: boolean;
  @Output() public result: EventEmitter<IUserDetailResult> = new EventEmitter<IUserDetailResult>();

  @ViewChild("userDetail") userDetail: TemplateRef<any> = {} as TemplateRef<any>;

  public userForm: UntypedFormGroup;
  public modalRef: NgbModalRef;
  public title: string;
  public acceptButtonCaption: string;
  public showApplicationSpecificFields: boolean;
  public editUsername: boolean;
  public editName: boolean;
  public addFromExistingUser: boolean;
  public isDuplicateUser: boolean;
  public isAlreadyAssignedUser: boolean;
  public assignedAgency: string;
  public configWorking: boolean;
  public roleCheckboxItems: CheckboxItem[];
  public selectedRolesIn: string[];
  public selectedRolesOut: string[];
  public errors: string[];

  private action: UserAction;
  private appId: number;
  private userId: string;
  
  public get username() {
    return this.userForm.get("username");
  }

  public get firstName() {
    return this.userForm.get("firstName");
  }

  public get lastName() {
    return this.userForm.get("lastName");
  }

  public get allowLogin() {
    return this.userForm.get("allowLogin");
  }

  public get displayName() {
    return this.userForm.get("displayName");
  }

  public get config() {
    return this.userForm.get("config");
  }

  public get allowAppLogin() {
    return this.userForm.get("allowAppLogin");
  }

  constructor(
    private readonly modalService: NgbModal,
    private readonly fb: UntypedFormBuilder,
    private readonly roleService: RoleService,
    private readonly userService: UserService
  ) { }

  ngOnInit(): void {
    this.userForm = this.fb.group({
      username: [""],
      firstName: ["", [Validators.required]],
      lastName: ["", [Validators.required]],
      displayName: [""],
      allowLogin: [""],
      allowAppLogin: [""]
    });
  }

  public open(appId: number, user: IUserDetail | null, showAppFields: boolean): void {
    this.appId = appId;
    this.editUsername = true;
    this.editName = true;
    this.isDuplicateUser = false;
    this.isAlreadyAssignedUser = false;
    this.assignedAgency = null;
    this.addFromExistingUser = false;
    this.errors = new Array<string>();

    if (user == null) {
      this.action = UserAction.AddNew;
      this.userId = null;
      this.username.setValue("");
      this.username.setValidators([Validators.required, Validators.email]);
      this.firstName.setValue("");
      this.lastName.setValue("");
      this.allowLogin.setValue(true);
      this.displayName.setValue("");
      this.allowAppLogin.setValue(true);
      this.selectedRolesOut = new Array<string>();
      this.title = "New User";
      this.acceptButtonCaption = "Add";
    } else {
      this.action = UserAction.Update;
      this.userId = user.id;
      this.username.setValue(user.username);
      this.username.setValidators([Validators.required]);
      this.firstName.setValue(user.firstName);
      this.allowLogin.setValue(user.allowLogin);
      this.lastName.setValue(user.lastName);
      this.updateDisplayName();
      this.allowAppLogin.setValue(user.allowAppLogin);
      this.selectedRolesOut = user.roleIds;
      this.title = "User Details"
      this.acceptButtonCaption = "Save";
      this.editUsername = false;
    }

    this.username.markAsUntouched();
    this.firstName.markAsUntouched();
    this.lastName.markAsUntouched();
    this.allowLogin.markAsUntouched();
    
    if (showAppFields) {
      this.showApplicationSpecificFields = true;

      this.roleService.getRoles(appId)
        .subscribe(data => {
          this.roleCheckboxItems = data.map(x => new CheckboxItem(x.id, x.name));
          if (user == null)
            this.selectedRolesIn = new Array<string>();
          else
            this.selectedRolesIn = user.roleIds;
        }, error => {
          this.errors.push(error.message);
        });
    } else {
      this.showApplicationSpecificFields = false;
    }

    this.modalRef = this.modalService.open(this.userDetail, { backdrop: "static" });
  }

  public onSubmit(): void {
    this.modalRef.close();
    var user: IUserDetailResult = {
      action: this.action,
      user: {
        id: this.userId,
        username: this.username.value,
        firstName: this.firstName.value,
        lastName: this.lastName.value,
        displayName: this.displayName.value,
        allowLogin: this.allowLogin.value,
        roleIds: this.selectedRolesOut,
        allowAppLogin: this.allowAppLogin.value
      }
    }
    this.result.emit(user);
  }

  public onNameChange(): void {
    this.updateDisplayName();
  }

  public onRolesToggle(value: string[]) {
    this.selectedRolesOut = value;
  }

  public onUsernameChange() {
    if (this.username.hasError('required') || this.username.hasError('email'))
      return;

    this.userService.getUser(this.appId, this.username.value)
      .subscribe(data => {
        if (data) {
          if (this.action == UserAction.AddNew || this.action == UserAction.AddFromExisting) {
            this.action = UserAction.AddFromExisting;
            this.isDuplicateUser = this.isDuplicate(data.id);
            this.isAlreadyAssignedUser = !this.isDuplicateUser && this.isAlreadyAssigned(data);
            if (this.isAlreadyAssignedUser)
              this.assignedAgency = data.agencyName;
            this.addFromExistingUser = !this.isDuplicateUser && !this.isAlreadyAssignedUser;
            this.userId = data.id;
            this.username.setValue(data.username);
            this.firstName.setValue(data.firstName);
            this.lastName.setValue(data.lastName);
            this.allowLogin.setValue(data.allowLogin);
            this.updateDisplayName();
            this.allowAppLogin.setValue(data.allowLogin);
            this.selectedRolesIn = new Array<string>();
            this.selectedRolesOut = new Array<string>();
            this.editUsername = false;
            this.editName = false;
          } else {
            this.username.setErrors({'duplicate': true});
          }
        }
      }, error => {
        // It's OK if we get a 404... it just means the username doesn't already exist
        if (error.status != 404)
          this.errors.push(error.message);
      })
  }

  public onAllowLoginChange() {
    if (!this.showApplicationSpecificFields)
      return;

    if (!this.showAppAllowLogin) {
      this.allowAppLogin.setValue(this.allowLogin.value);
    }
  }

  public closeError(error: string): void {
    this.errors.splice(this.errors.indexOf(error), 1);
  }

  private updateDisplayName() {
    if (!this.firstName.value && !this.lastName.value)
      this.displayName.setValue("");
    else if (this.firstName.value && !this.lastName.value)
      this.displayName.setValue(this.firstName.value);
    else if (!this.firstName.value && this.lastName.value)
      this.displayName.setValue(this.lastName.value);
    else
      this.displayName.setValue(this.firstName.value + " " + this.lastName.value);
  }
}
